import type { AccountDetail, ActionId, DetailRow } from "../app/account-model.js";
import { el } from "../lib/dom.js";
import { icon } from "../lib/icon.js";
import { PILL, meter, thumb } from "./home.js";

/**
 * An account's detail (UX spec v3 §C.5, 13-account.html), pushed inside Home: a top bar
 * with back only (the name is the hero's), the hero, the actions, two stat tiles, the
 * month's rows. States: loading (the same blocks, masked, "Loading this account"),
 * default, nothing this month. Rows are not controls (a receipt lives in Activity:
 * "See all in Activity").
 */
export interface DetailHandlers {
  onBack(): void;
  onAction(id: ActionId, d: AccountDetail): void;
  onSeeAll(account: string): void;
}

let ids = 0;

export class DetailView {
  readonly topbar: HTMLElement;
  readonly body: HTMLElement;
  readonly back: HTMLButtonElement;
  readonly #h: DetailHandlers;
  readonly #note: HTMLElement;
  readonly #content: HTMLElement;
  readonly #titleId = `d-title-${String(++ids)}`;

  constructor(handlers: DetailHandlers) {
    this.#h = handlers;
    this.back = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back to Home" } }, [icon("i-chev-l")]);
    this.back.addEventListener("click", () => this.#h.onBack());
    this.topbar = el("div", { class: "topbar" }, [this.back, el("span", { class: "t", aria: { hidden: "true" } }), el("span", { class: "topbar-pad", aria: { hidden: "true" } })]);
    this.#note = el("span", { class: "vis-hidden", role: "status" });
    this.#content = el("div", { class: "dcontent" });
    this.body = el("div", { class: "body" }, [this.#note, this.#content, el("div", { class: "home-end", aria: { hidden: "true" } })]);
  }

  get titleId(): string {
    return this.#titleId;
  }

  /** The hero from what Home already knows; the rest masked until the account's lines land. */
  loading(d: AccountDetail): void {
    this.#note.textContent = "Loading this account";
    const placeholder: DetailRow = { key: "sk", initial: "·", title: "Loading payment", sub: "00 Sep · PayNow", amount: "S$00.00", credit: false };
    this.#paint({ ...d, rows: [placeholder, placeholder, placeholder], stats: d.stats.length > 0 || d.kind === "invest" ? d.stats : [{ label: "Spent this month", figure: "S$000.00", sub: "0 payments" }, { label: "Came in", figure: "S$0,000.00", sub: "0 payments" }] }, true);
    this.body.scrollTop = 0;
  }

  show(d: AccountDetail): void {
    this.#note.textContent = "";
    this.#paint(d, false);
  }

  #paint(d: AccountDetail, masked: boolean): void {
    const hero: HTMLElement[] = [
      el("div", { class: "nm", id: this.#titleId }, [d.title, ...(d.digits === "" ? [] : [" ", el("span", { class: "d" }, [d.digits])])]),
      el("div", { class: "big money" }, [d.big, ...(d.bigTail === undefined ? [] : [el("small", {}, [d.bigTail])])]),
    ];
    if (d.due !== undefined) hero.push(el("div", { class: "due" }, ["Make a minimum payment of ", el("span", { class: "money" }, [d.due.amount]), " ", el("span", { class: "nw" }, [d.due.by])]));
    if (d.pill !== undefined || d.gain !== undefined) {
      hero.push(
        el("div", { class: "pl" }, [
          ...(d.pill === undefined ? [] : [el("span", { class: PILL[d.pill.tone] }, [d.pill.text])]),
          ...(d.gain === undefined ? [] : [el("span", {}, [el("span", { class: "money" }, [d.gain]), " all time"])]),
        ]),
      );
    }
    if (d.asOf !== undefined) hero.push(el("div", { class: "asof" }, [d.asOf]));
    if (d.cards.length > 0 || d.meter !== undefined) {
      hero.push(el("div", { class: "cardrow" }, [...(d.cards.length === 0 ? [] : [el("span", { class: "minis" }, d.cards.map(thumb))]), ...(d.meter === undefined ? [] : [meter(d.meter)])]));
    }
    const acts = d.actions.map((a) => {
      const b = el("button", { class: "raw act", type: "button" }, [el("i", { aria: { hidden: "true" } }, [icon(a.icon)]), el("span", {}, [a.label])]);
      b.addEventListener("click", () => this.#h.onAction(a.id, d));
      return b;
    });
    const sec: HTMLElement[] = [el("h3", {}, [d.section])];
    if (d.seeAll !== undefined && d.rows.length > 0) {
      const all = el("button", { class: "raw more", type: "button" }, ["See all in Activity"]);
      const account = d.seeAll;
      all.addEventListener("click", () => this.#h.onSeeAll(account));
      sec.push(all);
    }
    const blocks: HTMLElement[] = [el("div", { class: "dhero" }, hero), el("div", { class: "acts2" }, acts)];
    if (d.stats.length > 0) {
      blocks.push(
        el("div", { class: "stats" }, d.stats.map((s) => el("div", { class: "stat2" }, [el("small", {}, [s.label]), el("b", { class: "money" }, [s.figure]), el("span", {}, [s.sub])]))),
      );
    }
    blocks.push(el("div", { class: "sec" }, sec));
    if (d.rows.length > 0) blocks.push(el("div", { class: "list feed" }, d.rows.map(row)));
    if (d.nothing !== undefined) blocks.push(el("div", { class: "nothing" }, [d.nothing]));
    this.#content.className = masked ? "dcontent skel-mask" : "dcontent";
    if (masked) this.#content.setAttribute("aria-hidden", "true");
    else this.#content.removeAttribute("aria-hidden");
    this.#content.replaceChildren(...blocks);
  }
}

function row(r: DetailRow): HTMLElement {
  return el("div", { class: "item" }, [
    el("span", { class: r.initial.length > 2 ? "ic tk" : "ic", aria: { hidden: "true" } }, [r.initial]),
    el("span", { class: "tx" }, [el("b", {}, [r.title]), el("small", {}, [r.sub])]),
    el("span", { class: r.credit ? "amt money pos" : "amt money" }, [r.amount, ...(r.small === undefined ? [] : [el("small", { class: r.small.up ? "pos" : "" }, [r.small.text])])]),
  ]);
}
